import { QueryKey, useQuery } from '@tanstack/react-query';
import { API_EndpointKey, API_EndpointParams, API_EndpointResult, callEndpoint, type APIResponse } from './api_v2_def';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCachedObjectValue } from '../hooks/useCachedObjectValue';
import env from '../../env';
import { useSessionStore } from '../../store/useSessionStore';

export type UseApiQueryOptions = {
	enabled?: boolean;
	interval?: number;
	intervalStartDelay?: number;
	queryKey?: QueryKey;
	staleTime?: number;
	clearDataOnReload?: boolean;
	clearDataOnError?: boolean;
};

export function useApiQuery<K extends API_EndpointKey>(
	key: K,
	params: API_EndpointParams<K>,
	options: UseApiQueryOptions = {},
) {
	const {
		enabled = true,
		interval = false,
		queryKey,
		staleTime,
		clearDataOnReload,
		clearDataOnError,
		intervalStartDelay,
	} = options;
	const clickPreventionRef = useRef<boolean>(false);
	const cachedParams = useCachedObjectValue(params);
	const [queryEnabled, setQueryEnabled] = useState<boolean>(intervalStartDelay ? false : enabled);

	const {
		refetch,
		isFetching,
		data: queryData,
	} = useQuery({
		queryKey: queryKey ? ['api', key, cachedParams, ...queryKey] : ['api', key, cachedParams],
		enabled: queryEnabled,
		refetchInterval: interval,
		queryFn: () => requestData(key, cachedParams),
		staleTime: staleTime,
	});

	const { errorCode, data, errorData, loadingData } = (() => {
		if (isFetching && clearDataOnReload) {
			return {
				loadingData: isFetching,
				data: undefined,
				errorData: undefined,
				errorCode: queryData?.code,
			};
		}

		const resp = queryData;
		if (!resp) {
			return {
				loadingData: isFetching,
				data: undefined,
				errorData: undefined,
				errorCode: undefined,
			};
		}

		if (resp.success) {
			return {
				loadingData: isFetching,
				data: resp.data as API_EndpointResult<K>,
				errorData: undefined,
				errorCode: resp.code,
			};
		}

		return {
			loadingData: isFetching,
			data: clearDataOnError ? undefined : undefined, // replace if you truly have fallback data
			errorData: resp.error,
			errorCode: resp.code,
		};
	})();

	const cachedData = useCachedObjectValue(data);

	const fetchData = useCallback(async () => {
		if (clickPreventionRef.current) return;
		clickPreventionRef.current = true;
		const r = await refetch({ throwOnError: false });
		const resp = r.data;
		clickPreventionRef.current = false;
		return resp;
	}, [refetch]);

	useEffect(() => {
		// eslint-disable-next-line
		if (!intervalStartDelay) return setQueryEnabled(enabled ? true : false);
		const enableTimeout = setTimeout(() => setQueryEnabled(true), intervalStartDelay);
		return () => {
			clearTimeout(enableTimeout);
		};
	}, [intervalStartDelay, enabled]);

	return {
		fetchData,
		data: cachedData,
		loadingData,
		errorData,
		errorCode: errorCode,
	};
}

export type UseApiMutationOptions = {
	clearDataOnReload?: boolean;
	clearDataOnError?: boolean;
};

export function useApiMutation<K extends API_EndpointKey>(key: K, options: UseApiMutationOptions = {}) {
	const { clearDataOnReload, clearDataOnError } = options;
	const [data, setData] = useState<API_EndpointResult<K> | undefined>(undefined);
	const [errorData, setErrorData] = useState<string | undefined>(undefined);
	const [loadingData, setLoadingData] = useState<boolean>(false);

	const clickPreventionRef = useRef<boolean>(false);

	const fetchData = useCallback(
		async (params: API_EndpointParams<K>) => {
			if (clickPreventionRef.current) return;
			clickPreventionRef.current = true;

			setLoadingData(true);

			if (clearDataOnReload) {
				setData(undefined);
			}
			const resp = await requestData(key, params);

			if (resp.success) {
				setErrorData(undefined);
				setData(resp.data as API_EndpointResult<K>);
			} else {
				setErrorData(resp.error);
				if (clearDataOnError) setData(undefined);
			}
			setLoadingData(false);
			clickPreventionRef.current = false;
			return resp;
		},
		[clearDataOnError, clearDataOnReload, key],
	);

	return {
		fetchData,
		data,
		loadingData,
		errorData,
	};
}

export async function requestData<K extends API_EndpointKey>(
	key: K,
	params: API_EndpointParams<K>,
): APIResponse<API_EndpointResult<K>> {
	const { method, url, queryParams, body, userSession, sessionKey } = callEndpoint(key, params);

	try {
		const fetchOptions: RequestInit = {
			method: method,
			credentials: 'include',
		};
		const requestHeaders: HeadersInit = new Headers();

		// NOTE: Handle session
		if (userSession) {
			let userSessionKey: string | undefined = sessionKey;
			if (!userSessionKey) {
				const { token } = useSessionStore.getState();
				if (!token) return { success: false, error: 'Session not initialized' };
				userSessionKey = token;
			}
			// requestHeaders.set('Authorization', `Bearer ${userSessionKey}`);
		}

		// NOTE: Handle query params
		const query = new URLSearchParams();
		if (queryParams) {
			for (const [k, v] of Object.entries(queryParams)) {
				if (v !== undefined) query.append(k, String(v));
			}
		}

		if (body) {
			fetchOptions.body = JSON.stringify(body);
			requestHeaders.set('Content-Type', 'application/json');
		}

		const data = await fetch(`${env.API_URL}${url}${query.size > 0 ? `?${query.toString()}` : ''}`, {
			...fetchOptions,
			headers: requestHeaders,
		});

		const text = await data.text();
		const json = text
			? (() => {
					try {
						return JSON.parse(text);
					} catch {
						return undefined;
					}
				})()
			: undefined;

		if (data.ok) {
			const jsonData = (json ?? undefined) as API_EndpointResult<K>;
			return {
				success: true,
				data: jsonData,
				dataRaw: text,
				code: data.status,
			};
		}
		if (data.status === 401) {
			return {
				success: false,
				error: 'Please log in to continue',
				code: data.status,
			};
		}
		if (data.status === 403) {
			return {
				success: false,
				error: `You're not authorized to view this data`,
				code: data.status,
			};
		}
		if (data.status === 429) {
			return {
				success: false,
				error: `Please try again in a couple seconds`,
				code: data.status,
			};
		}
		const errorMessage = json?.error ?? `Failed to fetch data ${data.status}: '${json?.message ?? json?.error}'`;

		return {
			success: false,
			error: errorMessage,
			code: data.status,
		};
	} catch (err) {
		console.error(err);
		const errorMessage = `Failed to fetch data '${
			err instanceof Error && 'message' in err ? err.message : String(err)
		}'`;
		return { success: false, error: errorMessage };
	}
}
